/**
 * Lokale SEO-pagina's: plaatsen in Noord-Holland, Zuid-Holland, Zeeland en
 * Utrecht. Elke plaats heeft eigen, unieke tekst (bezienswaardigheden,
 * bekende straten, weetjes) plus taxi-informatie en routes.
 *
 * De afstanden worden berekend uit coördinaten met een wegfactor, zodat de
 * getoonde vaste prijzen altijd de taximeter van het platform volgen.
 */

import { ZH_PLACES } from "./local-seo-zh";
import { ROUTE_FACTS } from "./route-data";
import { placeDistance } from "./place-distances";

export type LocalPlace = {
  slug: string;
  name: string;
  province: "Noord-Holland" | "Zuid-Holland" | "Zeeland" | "Utrecht";
  gemeente: string;
  gemeenteUrl: string;
  /** Slug van de gemeentepagina (indien aanwezig). */
  gemeenteSlug?: string;
  inhabitants?: number;
  lat: number;
  lng: number;
  /** Unieke introductie van twee zinnen. */
  intro: string;
  sights: string[];
  streets: string[];
  facts: string[];
  /** Waar in deze plaats vaak wordt opgehaald. */
  pickups: string[];
};

export const ANCHORS = {
  schiphol: { name: "Schiphol", lat: 52.3105, lng: 4.7683, airport: true },
  "rotterdam-the-hague-airport": {
    name: "Rotterdam The Hague Airport",
    lat: 51.9569,
    lng: 4.4372,
    airport: true,
  },
  "eindhoven-airport": { name: "Eindhoven Airport", lat: 51.4501, lng: 5.3745, airport: true },
  amsterdam: { name: "Amsterdam", lat: 52.3728, lng: 4.8936, airport: false },
  rotterdam: { name: "Rotterdam", lat: 51.9225, lng: 4.4792, airport: false },
  "den-haag": { name: "Den Haag", lat: 52.078, lng: 4.31, airport: false },
  utrecht: { name: "Utrecht", lat: 52.0907, lng: 5.1214, airport: false },
  breda: { name: "Breda", lat: 51.5866, lng: 4.7758, airport: false },
  // Grote NS-knooppunten: eigen zoekintentie ("taxi naar Rotterdam Centraal").
  "amsterdam-centraal": { name: "Amsterdam Centraal", lat: 52.3791, lng: 4.9003, airport: false },
  "rotterdam-centraal": { name: "Rotterdam Centraal", lat: 51.9244, lng: 4.4692, airport: false },
  "utrecht-centraal": { name: "Utrecht Centraal", lat: 52.0894, lng: 5.1102, airport: false },
  "den-haag-centraal": { name: "Den Haag Centraal", lat: 52.0806, lng: 4.3249, airport: false },
  // Luchthavens net over de grens; hierbij geldt de buitenlandtoeslag.
  "dusseldorf-airport": { name: "Düsseldorf Airport", lat: 51.2895, lng: 6.7668, airport: true },
  "brussels-airport": { name: "Brussels Airport", lat: 50.9014, lng: 4.4844, airport: true },
} as const;

export type AnchorKey = keyof typeof ANCHORS;

const ROAD_FACTOR = 1.25;

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number) {
  const R = 6371;
  const toRad = (v: number) => (v * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Wegafstand in hele kilometers tussen plaats en bestemming.
 * Eerst de gecontroleerde Google-routegegevens; alleen als een combinatie
 * ontbreekt volgt een schatting uit de coördinaten.
 */
export function roadKm(place: LocalPlace, anchor: AnchorKey): number {
  const fact = ROUTE_FACTS[place.slug]?.[anchor] ?? placeDistance(place.slug, anchor);
  if (fact) return fact.km;
  const a = ANCHORS[anchor];
  return Math.max(1, Math.round(haversineKm(place.lat, place.lng, a.lat, a.lng) * ROAD_FACTOR));
}

/**
 * Realistische rijtijd in minuten buiten de spits, uit dezelfde gecontroleerde
 * routegegevens. Zonder gegevens volgt een voorzichtige schatting.
 */
export function roadMinutes(place: LocalPlace, anchor: AnchorKey): number {
  const fact = ROUTE_FACTS[place.slug]?.[anchor] ?? placeDistance(place.slug, anchor);
  if (fact) return Math.max(10, fact.min);
  return Math.max(10, Math.round(roadKm(place, anchor) * 0.85 + 8));
}


const CORE_PLACES: LocalPlace[] = [
  // ─────────────────────────────── Noord-Holland
  {
    slug: "amsterdam",
    name: "Amsterdam",
    province: "Noord-Holland",
    gemeente: "Gemeente Amsterdam",
    gemeenteUrl: "https://www.amsterdam.nl",
    inhabitants: 933680,
    lat: 52.3728,
    lng: 4.8936,
    intro:
      "Amsterdam is met bijna een miljoen inwoners de grootste stad van Nederland en het drukste vertrekpunt voor luchthavenritten in de Randstad. Door de grachtengordel, de milieuzone en het beperkte aantal laad- en losplekken is een taxi met vaste prijs vaak sneller en rustiger dan zelf rijden.",
    sights: [
      "Rijksmuseum en het Van Gogh Museum aan het Museumplein",
      "Anne Frank Huis aan de Prinsengracht",
      "Koninklijk Paleis op de Dam",
      "Het Concertgebouw en de Vondelpark-ingangen",
    ],
    streets: ["Damrak", "Kalverstraat", "Prinsengracht", "Zuidas / Gustav Mahlerlaan"],
    facts: [
      "De binnenstad telt ruim 1.500 bruggen en circa 165 grachten.",
      "Op de Zuidas vertrekken doordeweeks de meeste zakelijke ritten naar Schiphol.",
    ],
    pickups: ["Amsterdam Centraal", "Station Zuid", "RAI Amsterdam", "hotels rond het Leidseplein"],
  },
  {
    slug: "haarlem",
    name: "Haarlem",
    province: "Noord-Holland",
    gemeente: "Gemeente Haarlem",
    gemeenteUrl: "https://www.haarlem.nl",
    inhabitants: 163000,
    lat: 52.3874,
    lng: 4.6462,
    intro:
      "Haarlem ligt dicht bij Schiphol en combineert een middeleeuwse binnenstad met smalle eenrichtingsstraten. Wie vanuit het centrum met bagage vertrekt, is met een taxi voor de deur beduidend sneller dan via het overstapje op station Spaarnwoude.",
    sights: [
      "De Grote of Sint-Bavokerk aan de Grote Markt",
      "Teylers Museum, het oudste museum van Nederland",
      "De Hallen en het Frans Hals Museum",
      "Molen De Adriaan aan het Spaarne",
    ],
    streets: ["Grote Houtstraat", "Gedempte Oude Gracht", "Zijlweg", "Kleverlaan"],
    facts: [
      "Haarlem staat bekend als bloemenstad door de bollenvelden in de Haarlemmermeerpolder.",
      "De hofjes in het centrum zijn vaak niet met de auto bereikbaar; ophalen gebeurt op de dichtstbijzijnde hoek.",
    ],
    pickups: ["Station Haarlem", "Grote Markt", "Spaarne Gasthuis", "Haarlem-Noord"],
  },
  {
    slug: "zaandam",
    name: "Zaandam",
    province: "Noord-Holland",
    gemeente: "Gemeente Zaanstad",
    gemeenteUrl: "https://www.zaanstad.nl",
    inhabitants: 78000,
    lat: 52.4389,
    lng: 4.8296,
    intro:
      "Zaandam is het bruisende hart van Zaanstad en het vertrekpunt voor veel bezoekers van de Zaanse Schans. Richting Schiphol rijdt u via de Coentunnel; hoe druk het daar is, verandert niets aan uw vaste prijs.",
    sights: [
      "De Zaanse Schans met werkende molens",
      "Het Zaans Museum en het Verkade Paviljoen",
      "Het opvallende Inntel Hotel bij het station",
      "Czaar Peterhuisje",
    ],
    streets: ["Gedempte Gracht", "Westzijde", "Provincialeweg", "Kalf"],
    facts: [
      "De Zaanstreek was in de zeventiende eeuw het eerste industriegebied van Europa.",
      "Reizigers naar de Zaanse Schans boeken vaak een retourrit omdat er nauwelijks parkeerplekken zijn.",
    ],
    pickups: ["Station Zaandam", "Zaanse Schans", "Zaandam-Zuid", "Westerkoog"],
  },
  {
    slug: "hoofddorp",
    name: "Hoofddorp",
    province: "Noord-Holland",
    gemeente: "Gemeente Haarlemmermeer",
    gemeenteUrl: "https://www.haarlemmermeergemeente.nl",
    inhabitants: 78000,
    lat: 52.3061,
    lng: 4.6907,
    intro:
      "Hoofddorp is de hoofdplaats van Haarlemmermeer en ligt letterlijk naast Schiphol, wat het de kortste luchthavenrit van de regio geeft. Veel bedrijven rond Beukenhorst boeken hier zakelijke ritten met vaste prijs en factuur.",
    sights: [
      "Het Cultuurgebouw met theater en bibliotheek",
      "Haarlemmermeersche Bos en het Big Spotters Hill",
      "Museum De Cruquius over de droogmaking",
      "Winkelcentrum Vier Meren",
    ],
    streets: ["Kruisweg", "Hoofdweg", "Concourslaan", "Bennebroekerweg"],
    facts: [
      "De Haarlemmermeer werd in 1852 drooggemalen en ligt ruim vier meter onder NAP.",
      "Op Big Spotters Hill kijken vliegtuigspotters uit over de Polderbaan.",
    ],
    pickups: ["Station Hoofddorp", "Beukenhorst kantoren", "Spaarne Gasthuis Hoofddorp", "Floriande"],
  },
  {
    slug: "alkmaar",
    name: "Alkmaar",
    province: "Noord-Holland",
    gemeente: "Gemeente Alkmaar",
    gemeenteUrl: "https://www.alkmaar.nl",
    inhabitants: 111000,
    lat: 52.6324,
    lng: 4.7534,
    intro:
      "Alkmaar is wereldberoemd om zijn kaasmarkt en fungeert als verzorgingsstad voor heel Noord-Holland boven het Noordzeekanaal. Vanaf hier is Schiphol met de A9 goed te doen, maar de nachtdienstregeling van de trein maakt een taxi voor vroege vluchten vrijwel altijd de praktische keuze.",
    sights: [
      "De Waag en de kaasmarkt op het Waagplein",
      "Stedelijk Museum Alkmaar",
      "Grote of Sint-Laurenskerk",
      "Bierkade en de Alkmaarse grachten",
    ],
    streets: ["Langestraat", "Laat", "Kennemerstraatweg", "Hoornseweg"],
    facts: [
      "De leus 'Alkmaar begint de victorie' verwijst naar het beleg van 1573.",
      "De kaasmarkt trekt van maart tot september tienduizenden bezoekers per seizoen.",
    ],
    pickups: ["Station Alkmaar", "Waagplein", "Noordwest Ziekenhuisgroep", "De Mare"],
  },
  {
    slug: "hilversum",
    name: "Hilversum",
    province: "Noord-Holland",
    gemeente: "Gemeente Hilversum",
    gemeenteUrl: "https://www.hilversum.nl",
    inhabitants: 92000,
    lat: 52.2292,
    lng: 5.1669,
    intro:
      "Hilversum is de mediastad van Nederland en ligt tussen de bossen en heide van het Gooi. Studio-opnames beginnen vaak vroeg of eindigen laat, waardoor besloten vervoer met vaste prijs hier veel wordt gebruikt.",
    sights: [
      "Beeld en Geluid met zijn gekleurde glasgevel",
      "Raadhuis van Dudok, icoon van de Nieuwe Bouwen",
      "Mediapark en de studio's",
      "Landgoed Zonnestraal",
    ],
    streets: ["Kerkbrink", "Groest", "'s-Gravelandseweg", "Soestdijkerstraatweg"],
    facts: [
      "Architect Willem Dudok ontwierp tientallen gebouwen in de stad.",
      "Hilversum heeft geen stadsrechten maar is wel de grootste plaats van het Gooi.",
    ],
    pickups: ["Station Hilversum", "Mediapark", "Tergooi ziekenhuis", "Hilversumse Meent"],
  },
  {
    slug: "amstelveen",
    name: "Amstelveen",
    province: "Noord-Holland",
    gemeente: "Gemeente Amstelveen",
    gemeenteUrl: "https://www.amstelveen.nl",
    inhabitants: 93000,
    lat: 52.3114,
    lng: 4.8701,
    intro:
      "Amstelveen grenst aan zowel Amsterdam als Schiphol en huisvest veel internationale gezinnen en hoofdkantoren. De rit naar de vertrekhal duurt bij normaal verkeer nauwelijks een kwartier.",
    sights: [
      "Cobra Museum voor Moderne Kunst",
      "Het Amsterdamse Bos met de Bosbaan",
      "Jan van Hasseltweg-park en de Poel",
      "Winkelcentrum Stadshart",
    ],
    streets: ["Amsterdamseweg", "Van der Hooplaan", "Beneluxbaan", "Handweg"],
    facts: [
      "Ruim een kwart van de inwoners heeft een internationale achtergrond.",
      "Het Amsterdamse Bos is bijna drie keer zo groot als het Vondelpark.",
    ],
    pickups: ["Stadshart Amstelveen", "Ziekenhuis Amstelland", "Bovenkerk", "Westwijk"],
  },
  {
    slug: "purmerend",
    name: "Purmerend",
    province: "Noord-Holland",
    gemeente: "Gemeente Purmerend",
    gemeenteUrl: "https://www.purmerend.nl",
    inhabitants: 82000,
    lat: 52.505,
    lng: 4.9597,
    intro:
      "Purmerend ligt in Waterland en is voor veel forenzen de rustige woonplaats naast Amsterdam. Voor vroege vluchten rijdt hier nog geen trein, waardoor een taxi naar Schiphol de standaardoplossing is.",
    sights: [
      "Koemarkt met terrassen",
      "Purmerends Museum",
      "De Kaasmarkt en het historische stadhuis",
      "Het Beusebos en de Purmerbos-omgeving",
    ],
    streets: ["Koemarkt", "Westerstraat", "Gedempte Where", "Purmersteenweg"],
    facts: [
      "De Purmer is een van de vier droogmakerijen rondom de stad.",
      "Purmerend kreeg in 1410 stadsrechten van hertog Willem VI.",
    ],
    pickups: ["Station Purmerend", "Koemarkt", "Dijklander Ziekenhuis", "Weidevenne"],
  },
  {
    slug: "hoorn",
    name: "Hoorn",
    province: "Noord-Holland",
    gemeente: "Gemeente Hoorn",
    gemeenteUrl: "https://www.hoorn.nl",
    inhabitants: 74000,
    lat: 52.6425,
    lng: 5.0597,
    intro:
      "Hoorn is een VOC-stad aan het Markermeer met een havenfront dat nauwelijks veranderd is. Vanuit West-Friesland is het bijna een uur rijden naar Schiphol, dus vertrektijden worden hier ruim gepland.",
    sights: [
      "Hoofdtoren aan de Veermanskade",
      "Westfries Museum aan de Roode Steen",
      "Museumstoomtram Hoorn–Medemblik",
      "Het Julianapark",
    ],
    streets: ["Grote Noord", "Roode Steen", "Veermanskade", "Provincialeweg"],
    facts: [
      "Kaap Hoorn in Zuid-Amerika is naar deze stad vernoemd.",
      "De haven wordt in de zomer druk bezocht door de bruine vloot.",
    ],
    pickups: ["Station Hoorn", "Roode Steen", "Dijklander Ziekenhuis Hoorn", "Blokker"],
  },
  {
    slug: "ijmuiden",
    name: "IJmuiden",
    province: "Noord-Holland",
    gemeente: "Gemeente Velsen",
    gemeenteUrl: "https://www.velsen.nl",
    inhabitants: 59000,
    lat: 52.4589,
    lng: 4.6197,
    intro:
      "IJmuiden is de zeehaven van Amsterdam en het vertrekpunt van de veerboot naar Newcastle. Cruise- en ferryreizigers boeken hier vaak een taxi met vaste prijs, omdat de terminal buiten het openbaar vervoer om ligt.",
    sights: [
      "De Zeesluis IJmuiden, de grootste zeesluis ter wereld",
      "Felison Terminal en de cruisekade",
      "Fort Island en het Forteiland",
      "Strand IJmuiderslag en de pier",
    ],
    streets: ["Lange Nieuwstraat", "Kennemerlaan", "Sluisplein", "Dokweg"],
    facts: [
      "De nieuwe zeesluis is 500 meter lang en 70 meter breed.",
      "De vissershaven levert nog dagelijks verse Noordzeevis.",
    ],
    pickups: ["Felison Cruise Terminal", "Lange Nieuwstraat", "Zeewijk", "Velsen-Noord"],
  },
  {
    slug: "heerhugowaard",
    name: "Heerhugowaard",
    province: "Noord-Holland",
    gemeente: "Gemeente Dijk en Waard",
    gemeenteUrl: "https://www.dijkenwaard.nl",
    inhabitants: 58000,
    lat: 52.6674,
    lng: 4.8291,
    intro:
      "Heerhugowaard is samen met Langedijk opgegaan in de gemeente Dijk en Waard en groeide van polderdorp uit tot forensenstad. De ligging aan de N242 maakt de rit naar Alkmaar en verder naar Schiphol overzichtelijk.",
    sights: [
      "Stadshart Heerhugowaard",
      "Stadsdeel Stad van de Zon met zonnewoningen",
      "Recreatiegebied Park van Luna",
      "Museum Broeker Veiling in Broek op Langedijk",
    ],
    streets: ["Middenweg", "Westtangent", "Beveland", "Zuidtangent"],
    facts: [
      "Stad van de Zon was bij oplevering een van de grootste CO2-neutrale wijken van Europa.",
      "De polder werd al in 1631 drooggelegd.",
    ],
    pickups: ["Station Heerhugowaard", "Stadshart", "Park van Luna", "De Noord"],
  },
  {
    slug: "den-helder",
    name: "Den Helder",
    province: "Noord-Holland",
    gemeente: "Gemeente Den Helder",
    gemeenteUrl: "https://www.denhelder.nl",
    inhabitants: 56000,
    lat: 52.9563,
    lng: 4.7601,
    intro:
      "Den Helder is de marinestad op de noordpunt van Noord-Holland en het vertrekpunt van de veerboot naar Texel. Ritten naar Schiphol duren hier ruim een uur, waardoor reizigers vaak in de nacht vertrekken.",
    sights: [
      "Marinemuseum en Museumhaven Willemsoord",
      "Fort Kijkduin met zeeaquarium",
      "Vuurtoren Lange Jaap",
      "Veerhaven naar Texel",
    ],
    streets: ["Beatrixstraat", "Middenweg", "Havenweg", "Koningstraat"],
    facts: [
      "Lange Jaap is met 63,45 meter de hoogste gietijzeren vuurtoren van Europa.",
      "De Koninklijke Marine heeft hier haar belangrijkste thuisbasis.",
    ],
    pickups: ["Station Den Helder", "Veerhaven TESO", "Willemsoord", "Julianadorp"],
  },
  {
    slug: "beverwijk",
    name: "Beverwijk",
    province: "Noord-Holland",
    gemeente: "Gemeente Beverwijk",
    gemeenteUrl: "https://www.beverwijk.nl",
    inhabitants: 42000,
    lat: 52.4847,
    lng: 4.6567,
    intro:
      "Beverwijk is bekend van de Bazaar, de grootste overdekte markt van Europa, en ligt pal naast het Noordzeekanaalgebied. Via de Velsertunnel is Schiphol goed bereikbaar, al kan de tunnel in de spits flink vertragen.",
    sights: [
      "De Bazaar Beverwijk",
      "Huis van Rooswijk en het Rooswijkpark",
      "Wijk aan Zee met het brede strand",
      "Kasteel Assumburg in Heemskerk",
    ],
    streets: ["Breestraat", "Zeestraat", "Parallelweg", "Velsertraverse"],
    facts: [
      "De Bazaar trekt in het weekend tienduizenden bezoekers.",
      "Wijk aan Zee is een van de weinige badplaatsen zonder duinparkeerheffing bij de boulevard.",
    ],
    pickups: ["Station Beverwijk", "De Bazaar", "Rode Kruis Ziekenhuis", "Wijk aan Zee"],
  },
  {
    slug: "heemskerk",
    name: "Heemskerk",
    province: "Noord-Holland",
    gemeente: "Gemeente Heemskerk",
    gemeenteUrl: "https://www.heemskerk.nl",
    inhabitants: 39000,
    lat: 52.5108,
    lng: 4.6714,
    intro:
      "Heemskerk ligt tussen de duinen en het open weidegebied van Kennemerland en heeft twee kastelen binnen de gemeentegrens. De plaats heeft geen directe treinverbinding naar Schiphol zonder overstap, wat een taxi voor vroege vluchten aantrekkelijk maakt.",
    sights: [
      "Slot Assumburg",
      "Kasteel Marquette met landgoed",
      "Duinen van Heemskerk richting Castricum aan Zee",
      "Oosterwijkerdijk en de oude dorpskern",
    ],
    streets: ["Kerkweg", "Rijksstraatweg", "Maerelaan", "Tolweg"],
    facts: [
      "Ontdekkingsreiziger Jacob van Heemskerck is naar deze plaats vernoemd.",
      "Het duingebied hier is onderdeel van Natura 2000-gebied Noord-Hollands Duinreservaat.",
    ],
    pickups: ["Station Heemskerk-Beverwijk", "Kasteel Marquette", "Centrum", "Broekpolder"],
  },
  {
    slug: "castricum",
    name: "Castricum",
    province: "Noord-Holland",
    gemeente: "Gemeente Castricum",
    gemeenteUrl: "https://www.castricum.nl",
    inhabitants: 36000,
    lat: 52.5474,
    lng: 4.6683,
    intro:
      "Castricum is een groene woongemeente met een eigen strandopgang en uitgestrekte duinen. Veel gezinnen vertrekken hier met bagage en fietsen richting de luchthaven of het strand van Bakkum.",
    sights: [
      "Castricum aan Zee en de duinovergang",
      "Bezoekerscentrum De Hoep",
      "Huis van Hilde, archeologiemuseum bij het station",
      "Landgoed Duin en Bosch",
    ],
    streets: ["Dorpsstraat", "Zeeweg", "Mient", "Provincialeweg"],
    facts: [
      "Huis van Hilde is genoemd naar een vrouw die hier rond het jaar 350 leefde.",
      "Vanaf het station loopt een van de bekendste duinwandelroutes van Noord-Holland.",
    ],
    pickups: ["Station Castricum", "Bakkum", "Duin en Bosch", "Limmen"],
  },
  {
    slug: "huizen",
    name: "Huizen",
    province: "Noord-Holland",
    gemeente: "Gemeente Huizen",
    gemeenteUrl: "https://www.huizen.nl",
    inhabitants: 42000,
    lat: 52.2989,
    lng: 5.2408,
    intro:
      "Huizen ligt aan het Gooimeer en is de grootste plaats van het Gooi zonder treinstation. Juist daarom is een taxi hier de meest gebruikte manier om met bagage bij Schiphol of Amsterdam te komen.",
    sights: [
      "Jachthaven Huizen aan het Gooimeer",
      "Oude Kerk en de historische dorpskern",
      "Huizer Museum",
      "Trapjesberg met uitzicht over het meer",
    ],
    streets: ["Havenstraat", "Ceintuurbaan", "Bestevaer", "Naarderstraat"],
    facts: [
      "Huizen was tot de afsluiting van de Zuiderzee een vissersdorp met een eigen klederdracht.",
      "De gemeente heeft geen spoorlijn, maar wel een van de drukste busverbindingen van het Gooi.",
    ],
    pickups: ["Havenstraat", "Ceintuurbaan", "Jachthaven", "Huizermaat"],
  },
  {
    slug: "bussum",
    name: "Bussum",
    province: "Noord-Holland",
    gemeente: "Gemeente Gooise Meren",
    gemeenteUrl: "https://www.gooisemeren.nl",
    inhabitants: 33000,
    lat: 52.2794,
    lng: 5.1622,
    intro:
      "Bussum vormt samen met Naarden en Muiden de gemeente Gooise Meren en heeft een compact, levendig winkelcentrum. De A1 ligt vlakbij, waardoor zowel Amsterdam als Utrecht binnen een half uur bereikbaar is.",
    sights: [
      "Vesting Naarden met sterrenschans",
      "Spiegel- en Brediuskwartier met villa's",
      "Theater 't Spant",
      "Muiderslot in nabijgelegen Muiden",
    ],
    streets: ["Nassaulaan", "Brinklaan", "Landstraat", "Huizerweg"],
    facts: [
      "De vesting Naarden is een van de best bewaarde vestingsteden van Europa.",
      "Bussum groeide na 1874 hard door de komst van de spoorlijn Amsterdam–Amersfoort.",
    ],
    pickups: ["Station Naarden-Bussum", "Brinklaan", "Vesting Naarden", "Bussum-Zuid"],
  },
  {
    slug: "volendam",
    name: "Volendam",
    province: "Noord-Holland",
    gemeente: "Gemeente Edam-Volendam",
    gemeenteUrl: "https://www.edam-volendam.nl",
    inhabitants: 36000,
    lat: 52.4956,
    lng: 5.0714,
    intro:
      "Volendam is met zijn dijk, klederdracht en palingrokerijen een van de bekendste dorpen van Nederland. Groepen die hier een dagtocht combineren met Schiphol of Amsterdam kiezen meestal voor een taxibusje met vaste prijs.",
    sights: [
      "De Dijk met haven en terrassen",
      "Volendams Museum",
      "Kaasboerderij Alida Hoeve",
      "Het historische Edam in de buurt",
    ],
    streets: ["Haven", "Julianaweg", "Zeestraat", "Dijk"],
    facts: [
      "De klederdracht van Volendam is wereldwijd het bekendste Nederlandse kostuum.",
      "Vanaf de haven vaart de veerpont naar Marken.",
    ],
    pickups: ["Haven / De Dijk", "Julianaweg", "Alida Hoeve", "Edam centrum"],
  },
  {
    slug: "schagen",
    name: "Schagen",
    province: "Noord-Holland",
    gemeente: "Gemeente Schagen",
    gemeenteUrl: "https://www.schagen.nl",
    inhabitants: 46000,
    lat: 52.7876,
    lng: 4.7972,
    intro:
      "Schagen is de marktstad van de Kop van Noord-Holland en bekend om de Westfriese Markt op donderdag. Door de afstand tot de Randstad boeken reizigers hier vaak ruim van tevoren een nachtelijke rit naar de luchthaven.",
    sights: [
      "Slotruïne en de Slottuin",
      "Westfriese Markt in de zomer",
      "Museum Vreeburg",
      "Strand van Petten en Callantsoog vlakbij",
    ],
    streets: ["Gedempte Gracht", "Markt", "Nieuwstraat", "Zuiderweg"],
    facts: [
      "De Westfriese Markt bestaat al sinds 1461.",
      "Schagen telt een van de weinige overgebleven kasteeltorens van Noord-Holland.",
    ],
    pickups: ["Station Schagen", "Markt", "Callantsoog", "Petten"],
  },

  // ─────────────────────────────── Zuid-Holland
  {
    slug: "rotterdam",
    name: "Rotterdam",
    province: "Zuid-Holland",
    gemeente: "Gemeente Rotterdam",
    gemeenteUrl: "https://www.rotterdam.nl",
    inhabitants: 664000,
    lat: 51.9225,
    lng: 4.4792,
    intro:
      "Rotterdam heeft de grootste haven van Europa en een skyline die je in geen andere Nederlandse stad vindt. Met cruiseterminal, Ahoy en twee luchthavens binnen bereik is besloten vervoer met vaste prijs hier dagelijkse kost.",
    sights: [
      "Erasmusbrug en de Kop van Zuid",
      "Markthal en Cube Houses",
      "Euromast met uitzicht over de haven",
      "SS Rotterdam en de Cruise Terminal",
    ],
    streets: ["Coolsingel", "Lijnbaan", "Witte de Withstraat", "Wilhelminakade"],
    facts: [
      "Het havengebied strekt zich over ruim veertig kilometer uit tot aan de Maasvlakte.",
      "Rotterdam Centraal verwerkt dagelijks meer dan 100.000 reizigers.",
    ],
    pickups: ["Rotterdam Centraal", "Cruise Terminal Wilhelminakade", "Ahoy", "Erasmus MC"],
  },
  {
    slug: "den-haag",
    name: "Den Haag",
    province: "Zuid-Holland",
    gemeente: "Gemeente Den Haag",
    gemeenteUrl: "https://www.denhaag.nl",
    inhabitants: 566000,
    lat: 52.078,
    lng: 4.31,
    intro:
      "Den Haag is regeringsstad, badplaats en internationale rechtsstad in één. Voor ambassades, congressen en Scheveningen wordt hier veel gereden met vaste prijzen en zakelijke facturen.",
    sights: [
      "Binnenhof en de Hofvijver",
      "Mauritshuis met het Meisje met de parel",
      "Vredespaleis",
      "Scheveningen met pier en boulevard",
    ],
    streets: ["Lange Voorhout", "Spui", "Denneweg", "Gevers Deynootweg"],
    facts: [
      "Den Haag is de enige grote Nederlandse stad direct aan zee.",
      "De stad huisvest meer dan 200 internationale organisaties.",
    ],
    pickups: ["Den Haag Centraal", "Station Hollands Spoor", "World Forum", "Scheveningen"],
  },
  {
    slug: "leiden",
    name: "Leiden",
    province: "Zuid-Holland",
    gemeente: "Gemeente Leiden",
    gemeenteUrl: "https://www.leiden.nl",
    inhabitants: 127000,
    lat: 52.1601,
    lng: 4.497,
    intro:
      "Leiden heeft de oudste universiteit van Nederland en een grachtenstelsel waar autoverkeer bewust wordt geweerd. Voor het Bio Science Park en het LUMC rijden dagelijks internationale reizigers van en naar Schiphol.",
    sights: [
      "Rijksmuseum van Oudheden",
      "Burcht van Leiden",
      "Hortus botanicus, de oudste van Nederland",
      "Museum De Lakenhal",
    ],
    streets: ["Breestraat", "Haarlemmerstraat", "Rapenburg", "Plesmanlaan"],
    facts: [
      "Rembrandt van Rijn werd in 1606 in Leiden geboren.",
      "Op 3 oktober viert de stad het Leidens Ontzet met haring en wittebrood.",
    ],
    pickups: ["Leiden Centraal", "LUMC", "Bio Science Park", "Rapenburg"],
  },
  {
    slug: "dordrecht",
    name: "Dordrecht",
    province: "Zuid-Holland",
    gemeente: "Gemeente Dordrecht",
    gemeenteUrl: "https://www.dordrecht.nl",
    inhabitants: 121000,
    lat: 51.8133,
    lng: 4.6901,
    intro:
      "Dordrecht is de oudste stad van Holland en ligt op een eiland tussen drie rivieren. De ligging aan de A16 maakt zowel Rotterdam The Hague Airport als Eindhoven Airport goed bereikbaar.",
    sights: [
      "Grote Kerk met de scheve toren",
      "Dordrechts Museum",
      "Nationaal Park De Biesbosch",
      "Villa Augustus in de oude watertoren",
    ],
    streets: ["Voorstraat", "Groenmarkt", "Wolwevershaven", "Spuiboulevard"],
    facts: [
      "Dordrecht kreeg in 1220 als eerste Hollandse stad stadsrechten.",
      "De Biesbosch is een van de weinige zoetwatergetijdengebieden van Europa.",
    ],
    pickups: ["Station Dordrecht", "Grote Kerk", "Albert Schweitzer ziekenhuis", "Sterrenburg"],
  },
  {
    slug: "zoetermeer",
    name: "Zoetermeer",
    province: "Zuid-Holland",
    gemeente: "Gemeente Zoetermeer",
    gemeenteUrl: "https://www.zoetermeer.nl",
    inhabitants: 126000,
    lat: 52.0575,
    lng: 4.4931,
    intro:
      "Zoetermeer groeide vanaf de jaren zeventig uit tot een van de grootste groeisteden van Nederland, met brede lanen en veel eigen wijken. De A12 brengt u snel richting Den Haag, Utrecht of de luchthaven.",
    sights: [
      "SnowWorld met indoor skibanen",
      "Stadstheater en het Stadshart",
      "Nederlands Vuurwerkmuseum",
      "Recreatiegebied Noord Aa",
    ],
    streets: ["Dorpsstraat", "Promenade", "Afrikaweg", "Zilverstraat"],
    facts: [
      "Zoetermeer had in 1962 nog geen 7.000 inwoners.",
      "Het Randstadrail-netwerk verbindt de stad met Den Haag en Rotterdam.",
    ],
    pickups: ["Station Zoetermeer", "Stadshart", "SnowWorld", "Rokkeveen"],
  },
  {
    slug: "delft",
    name: "Delft",
    province: "Zuid-Holland",
    gemeente: "Gemeente Delft",
    gemeenteUrl: "https://www.delft.nl",
    inhabitants: 106000,
    lat: 52.0116,
    lng: 4.3571,
    intro:
      "Delft combineert Delfts blauw, een grachtenstad en een technische universiteit met veel internationale studenten en onderzoekers. De binnenstad is grotendeels autoluw, dus taxi's halen op bij vaste opstapplaatsen.",
    sights: [
      "Nieuwe Kerk met koninklijke grafkelder",
      "Royal Delft aardewerkfabriek",
      "Vermeer Centrum Delft",
      "TU Delft-campus met de Bibliotheek",
    ],
    streets: ["Markt", "Oude Delft", "Beestenmarkt", "Mekelweg"],
    facts: [
      "Johannes Vermeer schilderde hier het Gezicht op Delft.",
      "De TU Delft telt meer dan 25.000 studenten.",
    ],
    pickups: ["Station Delft", "Markt", "TU Delft campus", "Reinier de Graaf ziekenhuis"],
  },
  {
    slug: "alphen-aan-den-rijn",
    name: "Alphen aan den Rijn",
    province: "Zuid-Holland",
    gemeente: "Gemeente Alphen aan den Rijn",
    gemeenteUrl: "https://www.alphenaandenrijn.nl",
    inhabitants: 113000,
    lat: 52.1289,
    lng: 4.6553,
    intro:
      "Alphen aan den Rijn ligt centraal in het Groene Hart, halverwege Leiden, Gouda en Utrecht. Die middenpositie maakt het een logisch vertrekpunt voor ritten naar zowel Schiphol als Rotterdam.",
    sights: [
      "Avifauna vogelpark",
      "Archeon, het archeologisch themapark",
      "Het Rijnplein en de Aarkade",
      "Zegerplas met strand",
    ],
    streets: ["Hooftstraat", "Aarkade", "Prins Bernhardlaan", "Eisenhowerlaan"],
    facts: [
      "Het Romeinse castellum Albaniana lag op deze plek aan de limes.",
      "Avifauna is een van de oudste vogelparken van Europa.",
    ],
    pickups: ["Station Alphen aan den Rijn", "Avifauna", "Alrijne ziekenhuis", "Kerk en Zanen"],
  },
  {
    slug: "gouda",
    name: "Gouda",
    province: "Zuid-Holland",
    gemeente: "Gemeente Gouda",
    gemeenteUrl: "https://www.gouda.nl",
    inhabitants: 75000,
    lat: 52.0116,
    lng: 4.7105,
    intro:
      "Gouda is wereldwijd bekend van de kaas, de stroopwafels en het rijkste gotische stadhuis van Nederland. De stad ligt op het kruispunt van de A12 en A20, waardoor beide luchthavens vlot bereikbaar zijn.",
    sights: [
      "Stadhuis op de Markt",
      "Sint-Janskerk met de Goudse Glazen",
      "Museum Gouda en de Goudse Waag",
      "Kaasmarkt in de zomer",
    ],
    streets: ["Markt", "Kleiweg", "Lange Tiendeweg", "Burgemeester Jamesplein"],
    facts: [
      "De Sint-Janskerk is met 123 meter de langste kerk van Nederland.",
      "De Goudse kaasmarkt bestaat sinds 1395.",
    ],
    pickups: ["Station Gouda", "Markt", "Groene Hart Ziekenhuis", "Goverwelle"],
  },
  {
    slug: "schiedam",
    name: "Schiedam",
    province: "Zuid-Holland",
    gemeente: "Gemeente Schiedam",
    gemeenteUrl: "https://www.schiedam.nl",
    inhabitants: 79000,
    lat: 51.9195,
    lng: 4.3888,
    intro:
      "Schiedam heeft de hoogste traditionele molens ter wereld en een jeneververleden dat overal terug te zien is. De stad grenst direct aan Rotterdam en ligt vlak bij Rotterdam The Hague Airport.",
    sights: [
      "De Nolet, De Kameel en de andere reuzenmolens",
      "Jenevermuseum",
      "Stedelijk Museum Schiedam",
      "Havens van Schiedam",
    ],
    streets: ["Hoogstraat", "Lange Haven", "Broersvest", "Nieuwe Haven"],
    facts: [
      "Molen De Noord is met ruim 33 meter een van de hoogste windmolens ter wereld.",
      "Schiedam wordt ook wel Zwart Nazareth genoemd vanwege het branderijverleden.",
    ],
    pickups: ["Station Schiedam Centrum", "Lange Haven", "Nieuwland", "Vlaardingerdijk"],
  },
  {
    slug: "vlaardingen",
    name: "Vlaardingen",
    province: "Zuid-Holland",
    gemeente: "Gemeente Vlaardingen",
    gemeenteUrl: "https://www.vlaardingen.nl",
    inhabitants: 74000,
    lat: 51.9123,
    lng: 4.3417,
    intro:
      "Vlaardingen is een oude haringstad aan de Nieuwe Maas met een authentieke Oude Haven. Vanaf hier rijdt u binnen een kwartier naar Rotterdam The Hague Airport en in ongeveer een uur naar Schiphol.",
    sights: [
      "Grote Kerk en de Oude Haven",
      "Museum Vlaardingen",
      "Het Hof met stadspark",
      "Broekpolder natuurgebied",
    ],
    streets: ["Hoogstraat", "Westhavenkade", "Burgemeester Verkadesingel", "Marathonweg"],
    facts: [
      "Vlaardingen was eeuwenlang de belangrijkste haringhaven van Nederland.",
      "De naam van het Vlaardingen-cultuur uit het neolithicum komt van vondsten hier.",
    ],
    pickups: ["Station Vlaardingen Centrum", "Oude Haven", "Holy", "Westwijk"],
  },
  {
    slug: "capelle-aan-den-ijssel",
    name: "Capelle aan den IJssel",
    province: "Zuid-Holland",
    gemeente: "Gemeente Capelle aan den IJssel",
    gemeenteUrl: "https://www.capelleaandenijssel.nl",
    inhabitants: 67000,
    lat: 51.9297,
    lng: 4.5772,
    intro:
      "Capelle aan den IJssel grenst aan Rotterdam en heeft met de Rivium een groot kantorengebied aan de Nieuwe Maas. Zakelijke reizigers rijden hier veel naar Rotterdam The Hague Airport.",
    sights: [
      "Dorpsstraat langs de IJsseldijk",
      "Isala Theater",
      "Park Hitland met golfbaan",
      "Rivium met de parkshuttle",
    ],
    streets: ["Dorpsstraat", "Rivium Boulevard", "Kerklaan", "Bermweg"],
    facts: [
      "De ParkShuttle op Rivium was een van de eerste zelfrijdende busdiensten van Europa.",
      "De IJsseldijk beschermt het laagste punt van Nederland vlakbij Nieuwerkerk.",
    ],
    pickups: ["Rivium kantoren", "Metro Capelle Centrum", "IJsselland Ziekenhuis", "Schollevaar"],
  },
  {
    slug: "katwijk",
    name: "Katwijk",
    province: "Zuid-Holland",
    gemeente: "Gemeente Katwijk",
    gemeenteUrl: "https://www.katwijk.nl",
    inhabitants: 66000,
    lat: 52.2036,
    lng: 4.3992,
    intro:
      "Katwijk aan Zee is een badplaats met een brede boulevard, een ondergrondse parkeergarage in de duinen en veel vakantiegasten. Er is geen treinstation, waardoor een taxi naar Schiphol hier de gewoonste zaak van de wereld is.",
    sights: [
      "Boulevard en het strand van Katwijk aan Zee",
      "Vuurbaak, de oude bakenttoren",
      "Uitwateringssluizen van de Oude Rijn",
      "Duinen richting Noordwijk",
    ],
    streets: ["Boulevard", "Princestraat", "Zeeweg", "Tramstraat"],
    facts: [
      "De parkeergarage onder de boulevard ligt volledig in de zeewering.",
      "Katwijk heeft geen spoorlijn; de RijnGouwelijn kwam er nooit.",
    ],
    pickups: ["Boulevard Katwijk", "Katwijk-Binnen", "Rijnsburg", "Valkenburg"],
  },
  {
    slug: "rijswijk",
    name: "Rijswijk",
    province: "Zuid-Holland",
    gemeente: "Gemeente Rijswijk",
    gemeenteUrl: "https://www.rijswijk.nl",
    inhabitants: 56000,
    lat: 52.0365,
    lng: 4.3247,
    intro:
      "Rijswijk ligt ingeklemd tussen Den Haag en Delft en heeft met de Plaspoelpolder een van de oudste bedrijventerreinen van Nederland. De ligging aan de A4 maakt de rit naar Schiphol kort en voorspelbaar.",
    sights: [
      "Museum Rijswijk",
      "Landgoed Te Werve",
      "Oude Kerk aan de Herenstraat",
      "Winkelcentrum In de Bogaard",
    ],
    streets: ["Herenstraat", "Generaal Spoorlaan", "Haagweg", "Diepenhorstlaan"],
    facts: [
      "In 1697 werd hier de Vrede van Rijswijk getekend.",
      "De Plaspoelpolder telt honderden bedrijven op loopafstand van station Rijswijk.",
    ],
    pickups: ["Station Rijswijk", "Plaspoelpolder", "In de Bogaard", "Oud-Rijswijk"],
  },
  {
    slug: "barendrecht",
    name: "Barendrecht",
    province: "Zuid-Holland",
    gemeente: "Gemeente Barendrecht",
    gemeenteUrl: "https://www.barendrecht.nl",
    inhabitants: 49000,
    lat: 51.8561,
    lng: 4.5347,
    intro:
      "Barendrecht is bekend als groente- en fruitknooppunt van Nederland en ligt direct aan de A29 onder Rotterdam. De rit naar Rotterdam The Hague Airport duurt normaal gesproken een klein half uur.",
    sights: [
      "Dorpskern rond de Dorpsstraat",
      "Fruitteeltgebied de Zuidpolder",
      "Theater Het Kruispunt",
      "Recreatiegebied Zuidelijk Randpark",
    ],
    streets: ["Dorpsstraat", "Middenbaan", "Voordijk", "Koedood"],
    facts: [
      "Een groot deel van de Nederlandse groente- en fruithandel loopt via Barendrecht.",
      "De HSL en de Betuweroute lopen hier ondergronds door de bebouwde kom.",
    ],
    pickups: ["Station Barendrecht", "Middenbaan", "Carnisselande", "Bedrijventerrein Dierenstein"],
  },
  {
    slug: "ridderkerk",
    name: "Ridderkerk",
    province: "Zuid-Holland",
    gemeente: "Gemeente Ridderkerk",
    gemeenteUrl: "https://www.ridderkerk.nl",
    inhabitants: 46000,
    lat: 51.8722,
    lng: 4.6019,
    intro:
      "Ridderkerk ligt op het eiland IJsselmonde bij het beruchte knooppunt Ridderkerk waar A15 en A16 samenkomen. Wie hier op tijd bij een vlucht wil zijn, houdt rekening met de spits rond de Van Brienenoordbrug.",
    sights: [
      "Havenhoofd en de Noord",
      "Molen De Kortlandse Molen",
      "Reijerpark met sportcomplex",
      "Oosterpark en de dijkroute langs de Nieuwe Maas",
    ],
    streets: ["Sint Jorisstraat", "Rijnsingel", "Rotterdamseweg", "Vlietlaan"],
    facts: [
      "Het knooppunt Ridderkerk is een van de drukste verkeerspunten van Nederland.",
      "De gemeente bestaat uit de kernen Ridderkerk, Rijsoord, Bolnes en Slikkerveer.",
    ],
    pickups: ["Centrum Ridderkerk", "Slikkerveer", "Bolnes", "Rijsoord"],
  },
  {
    slug: "rhoon",
    name: "Rhoon",
    province: "Zuid-Holland",
    gemeente: "Gemeente Albrandswaard",
    gemeenteUrl: "https://www.albrandswaard.nl",
    inhabitants: 17000,
    lat: 51.8556,
    lng: 4.4197,
    intro:
      "Rhoon ligt onder de rook van Rotterdam met een eigen kasteel, een jachthaven en de groene Rhoonse Grienden. Het dorp heeft wel een metrohalte maar geen nachtvervoer, waardoor een taxi met vaste prijs voor vroege vluchten de logische keuze is.",
    sights: [
      "Kasteel Rhoon met landgoed en restaurant",
      "Rhoonse Grienden, getijdennatuur langs de Oude Maas",
      "Jachthaven Rhoon",
      "Dorpskerk aan de Dorpsdijk",
    ],
    streets: ["Dorpsdijk", "Rijsdijk", "Julianastraat", "Werkersdijk"],
    facts: [
      "Het kasteel dateert in oorsprong uit de veertiende eeuw en is nog altijd in gebruik.",
      "Rhoon vormt samen met Poortugaal en Portland de gemeente Albrandswaard.",
    ],
    pickups: ["Metrostation Rhoon", "Kasteel Rhoon", "Portland", "Poortugaal"],
  },
  {
    slug: "spijkenisse",
    name: "Spijkenisse",
    province: "Zuid-Holland",
    gemeente: "Gemeente Nissewaard",
    gemeenteUrl: "https://www.nissewaard.nl",
    inhabitants: 74000,
    lat: 51.845,
    lng: 4.3294,
    intro:
      "Spijkenisse is de grootste kern van Nissewaard op Voorne-Putten, verbonden met Rotterdam via de metro en de Spijkenisserbrug. Bij brugopeningen loopt het verkeer snel vast, dus voor vluchten wordt hier ruim gepland.",
    sights: [
      "Boekenberg, de bibliotheek onder glas",
      "Molen Nooitgedacht",
      "Theater De Stoep",
      "Recreatiegebied Bernisse",
    ],
    streets: ["Voorstraat", "Hekelingseweg", "Groenewoudlaan", "Marrewijklaan"],
    facts: [
      "De Boekenberg won internationale architectuurprijzen om zijn glazen piramidevorm.",
      "Nissewaard ontstond in 2015 uit de fusie van Spijkenisse en Bernisse.",
    ],
    pickups: ["Metro Spijkenisse Centrum", "Boekenberg", "Sterrenkwartier", "Hekelingen"],
  },
  {
    slug: "hellevoetsluis",
    name: "Hellevoetsluis",
    province: "Zuid-Holland",
    gemeente: "Gemeente Voorne aan Zee",
    gemeenteUrl: "https://www.voorneaanzee.nl",
    inhabitants: 40000,
    lat: 51.8322,
    lng: 4.1361,
    intro:
      "Hellevoetsluis is een vestingstad aan het Haringvliet met een grote jachthaven en een marineverleden. Het openbaar vervoer richting de luchthavens vraagt hier meerdere overstappen, wat een directe taxi aantrekkelijk maakt.",
    sights: [
      "Vesting Hellevoetsluis met droogdok",
      "Museum Het Vestingmuseum",
      "Haringvlietdam en de sluizen",
      "Brielle op een kwartier rijden",
    ],
    streets: ["Rijkstraatweg", "Oostzanddijk", "Opzoomerlaan", "Kanaalweg Oostzijde"],
    facts: [
      "De vesting was eeuwenlang thuishaven van de Admiraliteit van de Maze.",
      "Sinds 2023 valt Hellevoetsluis onder de gemeente Voorne aan Zee.",
    ],
    pickups: ["Vesting", "Jachthaven", "Nieuw-Helvoet", "Winkelcentrum Struytse Hoeck"],
  },
  {
    slug: "maassluis",
    name: "Maassluis",
    province: "Zuid-Holland",
    gemeente: "Gemeente Maassluis",
    gemeenteUrl: "https://www.maassluis.nl",
    inhabitants: 34000,
    lat: 51.9231,
    lng: 4.2497,
    intro:
      "Maassluis ligt aan de Nieuwe Waterweg waar de grootste zeeschepen vlak langs de kade varen. De metro brengt u naar Rotterdam, maar voor vluchten en cruises kiest men hier meestal een taxi voor de deur.",
    sights: [
      "Groote Kerk met het Garrels-orgel",
      "Nationaal Sleepvaart Museum",
      "Veerpont naar Rozenburg",
      "Havens en de Furie-kade",
    ],
    streets: ["Noordvliet", "Zuiddijk", "Veerstraat", "Westlandseweg"],
    facts: [
      "De sleepboot Furie uit 1916 ligt hier als varend monument.",
      "Maassluis ontstond in de vijftiende eeuw als sluisplaats voor Maasland.",
    ],
    pickups: ["Metro Maassluis Centrum", "Havens", "Steendijkpolder", "Sluispolder"],
  },
  {
    slug: "naaldwijk",
    name: "Naaldwijk",
    province: "Zuid-Holland",
    gemeente: "Gemeente Westland",
    gemeenteUrl: "https://www.gemeentewestland.nl",
    inhabitants: 20000,
    lat: 51.9931,
    lng: 4.2075,
    intro:
      "Naaldwijk is het bestuurlijke hart van het Westland, de glazen stad met de grootste kassenconcentratie ter wereld. Internationale handelspartners van de bloemen- en groentesector reizen hier dagelijks van en naar Schiphol.",
    sights: [
      "Oude Kerk en de Wilhelminaplein-omgeving",
      "Historisch Archief Westland",
      "Kassengebied en de veilingroute",
      "Strand van 's-Gravenzande vlakbij",
    ],
    streets: ["Herenstraat", "Dijkweg", "Middel Broekweg", "Zuidweg"],
    facts: [
      "Het Westland telt meer dan 2.500 hectare glastuinbouw.",
      "Vanuit deze regio vertrekt een groot deel van de Europese groente-export.",
    ],
    pickups: ["Centrum Naaldwijk", "Bedrijventerrein Honderdland", "'s-Gravenzande", "Wateringen"],
  },
  {
    slug: "zwijndrecht",
    name: "Zwijndrecht",
    province: "Zuid-Holland",
    gemeente: "Gemeente Zwijndrecht",
    gemeenteUrl: "https://www.zwijndrecht.nl",
    inhabitants: 45000,
    lat: 51.8175,
    lng: 4.6383,
    intro:
      "Zwijndrecht ligt tegenover Dordrecht aan de Oude Maas en is via de A16 met alles verbonden. Voor Eindhoven Airport en Rotterdam The Hague Airport is dit een gunstig vertrekpunt.",
    sights: [
      "Veerplein en de waterbus naar Dordrecht",
      "Develpark",
      "Oude Maas natuurgebied",
      "Molen Kijck over den Dijck",
    ],
    streets: ["Rotterdamseweg", "Ringdijk", "Burgemeester de Bruïnelaan", "Develsingel"],
    facts: [
      "De waterbus verbindt Zwijndrecht met de Dordtse binnenstad.",
      "Het Develgebied is een van de oudste veenstroompjes van de regio.",
    ],
    pickups: ["Station Zwijndrecht", "Veerplein", "Walburg", "Heerjansdam"],
  },
  {
    slug: "gorinchem",
    name: "Gorinchem",
    province: "Zuid-Holland",
    gemeente: "Gemeente Gorinchem",
    gemeenteUrl: "https://www.gorinchem.nl",
    inhabitants: 38000,
    lat: 51.8372,
    lng: 4.9747,
    intro:
      "Gorinchem is een vestingstad aan de Merwede met uitzicht op Slot Loevestein. De A15 en A27 kruisen elkaar hier, wat ritten naar Utrecht, Rotterdam en Eindhoven Airport vlot maakt.",
    sights: [
      "Vestingwallen en de Molen Nooit Volmaakt",
      "Slot Loevestein aan de overkant",
      "Gorcums Museum",
      "Grote Toren aan de Grote Markt",
    ],
    streets: ["Langendijk", "Grote Markt", "Arkelstraat", "Banneweg"],
    facts: [
      "Hugo de Groot ontsnapte in 1621 in een boekenkist uit nabijgelegen Loevestein.",
      "De vesting is nagenoeg compleet bewaard gebleven.",
    ],
    pickups: ["Station Gorinchem", "Grote Markt", "Beatrix Ziekenhuis", "Lingewijk"],
  },
  {
    slug: "berkel-en-rodenrijs",
    name: "Berkel en Rodenrijs",
    province: "Zuid-Holland",
    gemeente: "Gemeente Lansingerland",
    gemeenteUrl: "https://www.lansingerland.nl",
    inhabitants: 26000,
    lat: 51.9931,
    lng: 4.4783,
    intro:
      "Berkel en Rodenrijs is de grootste kern van Lansingerland en ligt dicht bij Rotterdam The Hague Airport. Reizigers waarderen de voorspelbare rit naar de terminal, ook 's nachts.",
    sights: [
      "Landscheiding en de Rottemeren",
      "Dorpsstraat met historische panden",
      "Glastuinbouwgebied Overbuurtse Polder",
      "Recreatieplas de Bergse Bos vlakbij",
    ],
    streets: ["Dorpsstraat", "Herenstraat", "Klapwijkseweg", "Oostmeerlaan"],
    facts: [
      "Lansingerland ontstond in 2007 uit de fusie van Berkel en Rodenrijs, Bergschenhoek en Bleiswijk.",
      "De RandstadRail-halte ligt midden in de nieuwbouwwijk Meerpolder.",
    ],
    pickups: ["RandstadRail Berkel Westpolder", "Dorpsstraat", "Bergschenhoek", "Bleiswijk"],
  },
  {
    slug: "leidschendam",
    name: "Leidschendam",
    province: "Zuid-Holland",
    gemeente: "Gemeente Leidschendam-Voorburg",
    gemeenteUrl: "https://www.lv.nl",
    inhabitants: 76000,
    lat: 52.0847,
    lng: 4.3956,
    intro:
      "Leidschendam-Voorburg ligt tegen Den Haag aan en heeft met Voorburg een van de oudste woonplaatsen van Nederland binnen de grenzen. Via de A4 rijdt u in ongeveer een half uur naar Schiphol.",
    sights: [
      "Winkelcentrum Leidsenhage / Mall of the Netherlands",
      "Huygensmuseum Hofwijck in Voorburg",
      "Landgoed Duivenvoorde",
      "Vlietland recreatiegebied",
    ],
    streets: ["Damlaan", "Herenstraat Voorburg", "Prinses Mariannelaan", "Noordsingel"],
    facts: [
      "Voorburg ontstond uit het Romeinse Forum Hadriani.",
      "De Vliet is een van de oudste gegraven kanalen van Nederland.",
    ],
    pickups: ["Mall of the Netherlands", "Station Voorburg", "Vlietland", "Damlaan"],
  },
  {
    slug: "pijnacker",
    name: "Pijnacker",
    province: "Zuid-Holland",
    gemeente: "Gemeente Pijnacker-Nootdorp",
    gemeenteUrl: "https://www.pijnacker-nootdorp.nl",
    inhabitants: 25000,
    lat: 52.0197,
    lng: 4.4319,
    intro:
      "Pijnacker ligt tussen Delft, Zoetermeer en Rotterdam, midden in een gebied van kassen en polders. Met RandstadRail is de regio bereikbaar, maar met bagage kiest men hier vaak voor een directe taxi.",
    sights: [
      "Klapwijkse Knoop en de Groenzoom",
      "Dorpskerk aan de Oostlaan",
      "Delfgauwse Wetering",
      "Balijbos richting Zoetermeer",
    ],
    streets: ["Oostlaan", "Ackershof", "Klapwijkseweg", "Katwijkerlaan"],
    facts: [
      "De Groenzoom is een natuurgebied dat speciaal is aangelegd tussen de nieuwbouwwijken.",
      "De gemeente bestaat uit Pijnacker, Nootdorp en Delfgauw.",
    ],
    pickups: ["RandstadRail Pijnacker Centrum", "Ackershof", "Nootdorp", "Delfgauw"],
  },
  {
    slug: "oud-beijerland",
    name: "Oud-Beijerland",
    province: "Zuid-Holland",
    gemeente: "Gemeente Hoeksche Waard",
    gemeenteUrl: "https://www.gemeentehw.nl",
    inhabitants: 24000,
    lat: 51.8256,
    lng: 4.4108,
    intro:
      "Oud-Beijerland is het winkelhart van de Hoeksche Waard, een polderlandschap met dijkdorpen ten zuiden van Rotterdam. Er rijdt geen trein, dus voor luchthavenritten wordt hier vrijwel altijd een taxi geboekt.",
    sights: [
      "Oude Kerk en het Poortje",
      "Molen De Windsnelheid",
      "Haven van Oud-Beijerland",
      "Nationaal Landschap Hoeksche Waard",
    ],
    streets: ["Molendijk", "Oostdijk", "Koninginneweg", "Beneden Molendijk"],
    facts: [
      "De Hoeksche Waard is aangewezen als Nationaal Landschap.",
      "De kern is aangelegd volgens een zeventiende-eeuws stratenplan.",
    ],
    pickups: ["Centrum Oud-Beijerland", "Haven", "Poortwijk", "Heinenoord"],
  },

  // ─────────────────────────────── Zeeland
  {
    slug: "middelburg",
    name: "Middelburg",
    province: "Zeeland",
    gemeente: "Gemeente Middelburg",
    gemeenteUrl: "https://www.middelburg.nl",
    inhabitants: 49000,
    lat: 51.4988,
    lng: 3.6109,
    intro:
      "Middelburg is de hoofdstad van Zeeland met een monumentale binnenstad rond de abdij en de Lange Jan. De afstand tot Schiphol is groot, dus reizigers vertrekken hier meestal midden in de nacht met een vaste prijs.",
    sights: [
      "Abdij van Middelburg met de Lange Jan-toren",
      "Stadhuis op de Markt",
      "Zeeuws Museum",
      "Miniatuur Walcheren",
    ],
    streets: ["Lange Delft", "Markt", "Segeerstraat", "Nieuwe Burg"],
    facts: [
      "De Lange Jan is bijna 91 meter hoog en al van ver over Walcheren zichtbaar.",
      "Middelburg telt meer dan 1.100 rijksmonumenten.",
    ],
    pickups: ["Station Middelburg", "Markt", "Adrz ziekenhuis", "Dauwendaele"],
  },
  {
    slug: "vlissingen",
    name: "Vlissingen",
    province: "Zeeland",
    gemeente: "Gemeente Vlissingen",
    gemeenteUrl: "https://www.vlissingen.nl",
    inhabitants: 44000,
    lat: 51.4426,
    lng: 3.5736,
    intro:
      "Vlissingen ligt aan de monding van de Westerschelde, waar de grootste containerschepen vlak langs de boulevard varen. Cruise- en zeevaartpassagiers boeken hier ritten naar Schiphol of Antwerpen.",
    sights: [
      "Boulevard met zicht op de Westerschelde",
      "Het Arsenaal",
      "Standbeeld van Michiel de Ruyter",
      "Fort Rammekens",
    ],
    streets: ["Walstraat", "Boulevard Bankert", "Badhuisstraat", "Koudekerkseweg"],
    facts: [
      "Michiel de Ruyter werd hier in 1607 geboren.",
      "De boulevard van Vlissingen is de langste van Nederland.",
    ],
    pickups: ["Station Vlissingen", "Boulevard", "Scheldekwartier", "Souburg"],
  },
  {
    slug: "goes",
    name: "Goes",
    province: "Zeeland",
    gemeente: "Gemeente Goes",
    gemeenteUrl: "https://www.goes.nl",
    inhabitants: 39000,
    lat: 51.5042,
    lng: 3.8889,
    intro:
      "Goes is het regionale centrum van Zuid-Beveland met een gezellige Grote Markt en een stoomtrein naar Borsele. De A58 verbindt de stad met Bergen op Zoom, Breda en verder richting Eindhoven Airport.",
    sights: [
      "Grote of Maria Magdalenakerk",
      "Stoomtrein Goes–Borsele",
      "Historische haven",
      "Museum Goes",
    ],
    streets: ["Lange Kerkstraat", "Grote Markt", "Klokstraat", "Ringbaan West"],
    facts: [
      "De stad kreeg in 1405 stadsrechten van graaf Willem VI.",
      "Rondom Goes ligt een van de belangrijkste fruitteeltgebieden van Nederland.",
    ],
    pickups: ["Station Goes", "Grote Markt", "Adrz Goes", "Ouverture"],
  },
  {
    slug: "terneuzen",
    name: "Terneuzen",
    province: "Zeeland",
    gemeente: "Gemeente Terneuzen",
    gemeenteUrl: "https://www.terneuzen.nl",
    inhabitants: 55000,
    lat: 51.3346,
    lng: 3.8281,
    intro:
      "Terneuzen ligt in Zeeuws-Vlaanderen aan het Kanaal Gent-Terneuzen en heeft de grootste sluizen van de regio. Antwerpen en Gent liggen dichterbij dan Rotterdam, wat de routekeuze bij internationale ritten bepaalt.",
    sights: [
      "Nieuwe Sluis Terneuzen",
      "Westerscheldetunnel-ingang",
      "Scheldeboulevard",
      "Het Portaal van Vlaanderen",
    ],
    streets: ["Noordstraat", "Axelsestraat", "Scheldekade", "Mr. F.J. Haarmanweg"],
    facts: [
      "De Westerscheldetunnel is met 6,127 kilometer de langste wegtunnel van Nederland.",
      "De nieuwe zeesluis is geschikt voor de grootste zeeschepen richting Gent.",
    ],
    pickups: ["Busstation Terneuzen", "Scheldeboulevard", "ZorgSaam ziekenhuis", "Sluiskil"],
  },
  {
    slug: "hulst",
    name: "Hulst",
    province: "Zeeland",
    gemeente: "Gemeente Hulst",
    gemeenteUrl: "https://www.gemeentehulst.nl",
    inhabitants: 27000,
    lat: 51.2794,
    lng: 4.0553,
    intro:
      "Hulst is een complete vestingstad vlak bij de Belgische grens en wordt de meest Vlaamse stad van Nederland genoemd. Antwerpen ligt op een half uur, Schiphol op ruim twee uur rijden.",
    sights: [
      "Volledig bewaarde vestingwallen",
      "Basiliek van Sint-Willibrordus",
      "Stadhuis op de Grote Markt",
      "Reynaertmonument",
    ],
    streets: ["Grote Markt", "Steenstraat", "Gentsestraat", "Absdaalseweg"],
    facts: [
      "De vesting heeft nog negen complete bastions.",
      "Hulst is het decor van het middeleeuwse verhaal Van den vos Reynaerde.",
    ],
    pickups: ["Grote Markt", "Busstation Hulst", "Vestingwallen", "Kloosterzande"],
  },
  {
    slug: "zierikzee",
    name: "Zierikzee",
    province: "Zeeland",
    gemeente: "Gemeente Schouwen-Duiveland",
    gemeenteUrl: "https://www.schouwen-duiveland.nl",
    inhabitants: 11000,
    lat: 51.6511,
    lng: 3.9192,
    intro:
      "Zierikzee is het historische havenstadje van Schouwen-Duiveland, met poorten, een onvoltooide toren en een levendige jachthaven. Via de Zeelandbrug en de N59 rijdt u richting Rotterdam en de Randstad.",
    sights: [
      "Sint-Lievensmonstertoren, nooit afgebouwd",
      "Noordhavenpoort en Zuidhavenpoort",
      "Stadhuismuseum",
      "Zeelandbrug",
    ],
    streets: ["Havenpark", "Dam", "Lange Nobelstraat", "Grachtweg"],
    facts: [
      "De Zeelandbrug was bij oplevering in 1965 de langste brug van Europa.",
      "Zierikzee telt ruim 500 rijksmonumenten in een compacte kern.",
    ],
    pickups: ["Havenpark", "Busstation Zierikzee", "Jachthaven", "Renesse"],
  },

  // ─────────────────────────────── Utrecht
  {
    slug: "utrecht",
    name: "Utrecht",
    province: "Utrecht",
    gemeente: "Gemeente Utrecht",
    gemeenteUrl: "https://www.utrecht.nl",
    inhabitants: 374000,
    lat: 52.0907,
    lng: 5.1214,
    intro:
      "Utrecht ligt precies in het midden van Nederland en heeft met de Domtoren het hoogste kerkgebouw van het land. Door de autoluwe binnenstad en beperkte parkeerruimte is een taxi met vaste prijs naar Schiphol vaak de rustigste optie.",
    sights: [
      "Domtoren en het Domplein",
      "Oudegracht met werfkelders",
      "Museum Speelklok",
      "Jaarbeurs en TivoliVredenburg",
    ],
    streets: ["Oudegracht", "Vredenburg", "Biltstraat", "Croeselaan"],
    facts: [
      "De Domtoren is met 112 meter de hoogste kerktoren van Nederland.",
      "Utrecht Centraal is het drukste treinstation van het land.",
    ],
    pickups: ["Utrecht Centraal", "Jaarbeurs", "UMC Utrecht", "Leidsche Rijn"],
  },
  {
    slug: "amersfoort",
    name: "Amersfoort",
    province: "Utrecht",
    gemeente: "Gemeente Amersfoort",
    gemeenteUrl: "https://www.amersfoort.nl",
    inhabitants: 161000,
    lat: 52.1561,
    lng: 5.3878,
    intro:
      "Amersfoort heeft een van de best bewaarde middeleeuwse binnensteden van Nederland, met muurhuizen en de Koppelpoort. De stad ligt op het knooppunt van de A1 en A28, waardoor Schiphol in ongeveer drie kwartier bereikbaar is.",
    sights: [
      "Koppelpoort, een unieke land-waterpoort",
      "Onze-Lieve-Vrouwetoren, de Lange Jan",
      "Mondriaanhuis",
      "Dierenpark Amersfoort",
    ],
    streets: ["Langestraat", "Hof", "Utrechtseweg", "Stadsring"],
    facts: [
      "De Amersfoortse Kei wordt sinds 1661 met de stad geassocieerd.",
      "Piet Mondriaan werd hier in 1872 geboren.",
    ],
    pickups: ["Station Amersfoort", "Hof", "Meander Medisch Centrum", "Vathorst"],
  },
  {
    slug: "veenendaal",
    name: "Veenendaal",
    province: "Utrecht",
    gemeente: "Gemeente Veenendaal",
    gemeenteUrl: "https://www.veenendaal.nl",
    inhabitants: 68000,
    lat: 52.0286,
    lng: 5.5544,
    intro:
      "Veenendaal ligt tussen de Utrechtse Heuvelrug en de Gelderse Vallei en heeft een sterk regionaal winkelcentrum. De A12 loopt langs de stad, wat de rit naar Utrecht en Schiphol overzichtelijk maakt.",
    sights: [
      "Museum Veenendaal",
      "Kwintelooijen, een oude zandafgraving",
      "Grutterij en het Oude Dorp",
      "Utrechtse Heuvelrug dichtbij",
    ],
    streets: ["Hoofdstraat", "Zandstraat", "Kerkewijk", "Rondweg-West"],
    facts: [
      "De naam verwijst naar de veenwinning die hier in de zestiende eeuw begon.",
      "Veenendaal heeft twee treinstations: Centrum en De Klomp.",
    ],
    pickups: ["Station Veenendaal Centrum", "Hoofdstraat", "Bedrijvenpark Het Ambacht", "Veenendaal-Oost"],
  },
  {
    slug: "nieuwegein",
    name: "Nieuwegein",
    province: "Utrecht",
    gemeente: "Gemeente Nieuwegein",
    gemeenteUrl: "https://www.nieuwegein.nl",
    inhabitants: 65000,
    lat: 52.0292,
    lng: 5.0806,
    intro:
      "Nieuwegein ontstond uit de dorpen Jutphaas en Vreeswijk en groeide uit tot een moderne stad aan het Amsterdam-Rijnkanaal. De sneltram verbindt met Utrecht, maar voor vroege vluchten rijdt hier een taxi met vaste prijs.",
    sights: [
      "Museumwerf Vreeswijk",
      "Oude Sluis van Vreeswijk",
      "Theater De Kom",
      "Park Oudegein met kasteelruïne",
    ],
    streets: ["Nedereindseweg", "Herenstraat Vreeswijk", "Zuidstedeweg", "Ravenswade"],
    facts: [
      "De sluizen van Vreeswijk behoren tot de oudste schutsluizen van Nederland.",
      "Nieuwegein bestaat pas sinds 1971 als zelfstandige gemeente.",
    ],
    pickups: ["Stadscentrum Nieuwegein", "St. Antonius Ziekenhuis", "Vreeswijk", "Ravenswade"],
  },
  {
    slug: "zeist",
    name: "Zeist",
    province: "Utrecht",
    gemeente: "Gemeente Zeist",
    gemeenteUrl: "https://www.zeist.nl",
    inhabitants: 65000,
    lat: 52.09,
    lng: 5.2333,
    intro:
      "Zeist ligt aan de rand van de Utrechtse Heuvelrug tussen bossen en landgoederen, met Slot Zeist als middelpunt. De plaats heeft geen treinstation, waardoor taxiritten naar Schiphol en Utrecht hier standaard zijn.",
    sights: [
      "Slot Zeist met barokke tuinen",
      "Broedergemeente en het Zusterplein",
      "Landgoed Beerschoten",
      "KNVB-campus",
    ],
    streets: ["Slotlaan", "1e Dorpsstraat", "Utrechtseweg", "Driebergseweg"],
    facts: [
      "De Evangelische Broedergemeente vestigde zich hier al in 1746.",
      "Zeist is de thuisbasis van de KNVB.",
    ],
    pickups: ["Slotlaan", "KNVB Campus", "Slot Zeist", "Den Dolder"],
  },
  {
    slug: "houten",
    name: "Houten",
    province: "Utrecht",
    gemeente: "Gemeente Houten",
    gemeenteUrl: "https://www.houten.nl",
    inhabitants: 51000,
    lat: 52.0347,
    lng: 5.1681,
    intro:
      "Houten staat internationaal bekend als fietsstad met een verkeersstructuur waarin de auto om de wijken heen rijdt. Juist daarom halen taxi's hier op vaste punten aan de rondweg of voor de deur via de wijkontsluiting op.",
    sights: [
      "Kasteel Heemstede-terrein",
      "Rond en Oude Dorp met de dorpskerk",
      "Fort Honswijk aan de Lek",
      "Rietplas met strandje",
    ],
    streets: ["Het Rond", "Rondweg", "Odijkseweg", "Molenzoom"],
    facts: [
      "Houten won meermaals de titel Fietsstad van Nederland.",
      "De Nieuwe Hollandse Waterlinie loopt langs de zuidkant van de gemeente.",
    ],
    pickups: ["Station Houten", "Het Rond", "Houten Castellum", "Schalkwijk"],
  },
  {
    slug: "woerden",
    name: "Woerden",
    province: "Utrecht",
    gemeente: "Gemeente Woerden",
    gemeenteUrl: "https://www.woerden.nl",
    inhabitants: 53000,
    lat: 52.0857,
    lng: 4.8836,
    intro:
      "Woerden ligt midden in het Groene Hart op een Romeins castellum en heeft een compleet kasteel in het centrum. Via de A12 bent u snel in Utrecht, Gouda of aan de A4 richting Schiphol.",
    sights: [
      "Kasteel Woerden",
      "Stadsmuseum Woerden",
      "Molen De Windhond",
      "Kaaspakhuis en de kaasmarkt",
    ],
    streets: ["Voorstraat", "Rijnstraat", "Utrechtsestraatweg", "Hollandbaan"],
    facts: [
      "Onder de binnenstad liggen resten van het Romeinse castellum Laurium.",
      "Woerden noemt zichzelf de kaasstad van het Groene Hart.",
    ],
    pickups: ["Station Woerden", "Kasteel Woerden", "Snel en Polanen", "Harmelen"],
  },
  {
    slug: "ijsselstein",
    name: "IJsselstein",
    province: "Utrecht",
    gemeente: "Gemeente IJsselstein",
    gemeenteUrl: "https://www.ijsselstein.nl",
    inhabitants: 34000,
    lat: 52.0206,
    lng: 5.0417,
    intro:
      "IJsselstein heeft een historische binnenstad met stadsmuur en is van ver herkenbaar aan de hoge zendmast. De sneltram loopt tot het centrum, maar met koffers rijdt men hier liever direct door naar de luchthaven.",
    sights: [
      "Zendmast IJsselstein, het hoogste bouwwerk van Nederland",
      "Oude Toren van de Nicolaaskerk",
      "Stadsmuseum IJsselstein",
      "Kasteel IJsselstein-restanten",
    ],
    streets: ["Utrechtsestraat", "Benschopperstraat", "Weidstraat", "Baronieweg"],
    facts: [
      "De zendmast meet ruim 372 meter en is daarmee het hoogste bouwwerk van Nederland.",
      "IJsselstein kreeg in 1331 stadsrechten.",
    ],
    pickups: ["Centrum IJsselstein", "Sneltramhalte Binnenstad", "Zenderpark", "Achterveld"],
  },
  {
    slug: "soest",
    name: "Soest",
    province: "Utrecht",
    gemeente: "Gemeente Soest",
    gemeenteUrl: "https://www.soest.nl",
    inhabitants: 47000,
    lat: 52.1736,
    lng: 5.2917,
    intro:
      "Soest ligt tussen de bossen van de Utrechtse Heuvelrug en de Eempolders, met Soestdijk als bekendste buurplaats. Vanaf hier rijdt u via de A28 en A1 in drie kwartier naar Schiphol.",
    sights: [
      "Paleis Soestdijk",
      "Soesterduinen, een zandverstuiving",
      "Museum Oud Soest",
      "Nationaal Militair Museum in Soesterberg",
    ],
    streets: ["Van Weedestraat", "Birkstraat", "Soesterbergsestraat", "Nieuweweg"],
    facts: [
      "Paleis Soestdijk was ruim vijftig jaar de woning van prinses Juliana en prins Bernhard.",
      "De Soesterduinen zijn een van de weinige actieve zandverstuivingen van Nederland.",
    ],
    pickups: ["Station Soest-Zuid", "Van Weedestraat", "Paleis Soestdijk", "Soesterberg"],
  },
  {
    slug: "maarssen",
    name: "Maarssen",
    province: "Utrecht",
    gemeente: "Gemeente Stichtse Vecht",
    gemeenteUrl: "https://www.stichtsevecht.nl",
    inhabitants: 40000,
    lat: 52.1394,
    lng: 5.0392,
    intro:
      "Maarssen ligt aan de Vecht tussen de buitenplaatsen die Amsterdamse kooplieden hier lieten bouwen. Dankzij de A2 zit u vanaf hier binnen een half uur op Schiphol.",
    sights: [
      "Buitenplaats Goudestein",
      "Vechtoevers met historische theekoepels",
      "Dorpskerk aan de Kerkweg",
      "Slot Zuylen bij Oud-Zuilen",
    ],
    streets: ["Langegracht", "Straatweg", "Zuilense Ring", "Diependaalsedijk"],
    facts: [
      "Langs de Vecht staan tientallen zeventiende-eeuwse buitenplaatsen.",
      "Stichtse Vecht ontstond in 2011 uit Maarssen, Breukelen en Loenen.",
    ],
    pickups: ["Station Maarssen", "Goudestein", "Maarssenbroek", "Breukelen"],
  },
  {
    slug: "bilthoven",
    name: "Bilthoven",
    province: "Utrecht",
    gemeente: "Gemeente De Bilt",
    gemeenteUrl: "https://www.debilt.nl",
    inhabitants: 44000,
    lat: 52.1333,
    lng: 5.2,
    intro:
      "Bilthoven is een groene woonplaats met villawijken tussen de bossen en het bekendste weerstation van Nederland vlakbij. De A27 en A28 liggen om de hoek, waardoor luchthavenritten hier vlot verlopen.",
    sights: [
      "KNMI-terrein in De Bilt",
      "Landgoed Beerschoten en Houdringe",
      "Het Lichtruim cultuurhuis",
      "RIVM-campus",
    ],
    streets: ["Julianalaan", "Soestdijkseweg", "Planetenbaan", "Hessenweg"],
    facts: [
      "Het KNMI meet in De Bilt sinds 1897 de officiële landelijke temperatuur.",
      "De gemeente De Bilt bestaat uit zes kernen, waaronder Bilthoven en Maartensdijk.",
    ],
    pickups: ["Station Bilthoven", "Julianalaan", "RIVM", "De Bilt centrum"],
  },
  {
    slug: "doorn",
    name: "Doorn",
    province: "Utrecht",
    gemeente: "Gemeente Utrechtse Heuvelrug",
    gemeenteUrl: "https://www.heuvelrug.nl",
    inhabitants: 10000,
    lat: 52.0333,
    lng: 5.35,
    intro:
      "Doorn ligt midden op de Utrechtse Heuvelrug en is bekend van Huis Doorn, waar de laatste Duitse keizer in ballingschap woonde. Het dorp heeft geen station, dus ritten naar Utrecht en Schiphol gaan hier per taxi.",
    sights: [
      "Huis Doorn met park",
      "Kaapse Bossen met uitkijktoren",
      "Landgoed Zonheuvel",
      "Nationaal Park Utrechtse Heuvelrug",
    ],
    streets: ["Dorpsstraat", "Amersfoortseweg", "Langbroekerweg", "Driebergsestraatweg"],
    facts: [
      "Keizer Wilhelm II woonde van 1920 tot 1941 op Huis Doorn.",
      "De Kaapse Bossen liggen op een stuwwal uit de voorlaatste ijstijd.",
    ],
    pickups: ["Dorpsstraat Doorn", "Huis Doorn", "Driebergen-Rijsenburg", "Leersum"],
  },
  {
    slug: "leerdam",
    name: "Leerdam",
    province: "Utrecht",
    gemeente: "Gemeente Vijfheerenlanden",
    gemeenteUrl: "https://www.vijfheerenlanden.nl",
    inhabitants: 21000,
    lat: 51.8931,
    lng: 5.0917,
    intro:
      "Leerdam is de glasstad van Nederland en hoort sinds 2019 bij de Utrechtse gemeente Vijfheerenlanden. Via de A15 en A27 rijdt u vanaf hier naar Utrecht, Rotterdam of Eindhoven Airport.",
    sights: [
      "Nationaal Glasmuseum",
      "Royal Leerdam Crystal",
      "Hofje van mevrouw Van Aerden",
      "Lingedijk richting Asperen",
    ],
    streets: ["Kerkstraat", "Nieuwstraat", "Lingedijk", "Industrieweg"],
    facts: [
      "Er wordt in Leerdam al sinds 1765 glas geblazen.",
      "Vijfheerenlanden ontstond uit Leerdam, Vianen en Zederik.",
    ],
    pickups: ["Station Leerdam", "Glascentrum", "Kerkstraat", "Vianen"],
  },
  {
    slug: "spaarndam",
    name: "Spaarndam",
    province: "Noord-Holland",
    gemeente: "Gemeente Haarlem",
    gemeenteUrl: "https://www.haarlem.nl",
    lat: 52.4122,
    lng: 4.6847,
    intro:
      "Spaarndam is een klein dijkdorp aan het Spaarne, met smalle straatjes rond de oude sluizen. Een taxi die voor de deur stopt is hier vaak handiger dan de bus, zeker met koffers richting Schiphol.",
    sights: ["Het beeldje van Hansje Brinker bij de sluis", "De Kolksluis in het Spaarne", "Het dijkdorp aan de Kerkstraat"],
    streets: ["Kerkstraat", "Westeinde", "Oostkolk"],
    facts: ["Spaarndam ligt deels in de gemeente Haarlem en deels in Haarlemmermeer."],
    pickups: ["Kerkstraat", "Spaarndam-West", "de sluis aan het Spaarne"],
  },
  {
    slug: "velserbroek",
    name: "Velserbroek",
    province: "Noord-Holland",
    gemeente: "Gemeente Velsen",
    gemeenteUrl: "https://www.velsen.nl",
    lat: 52.4458,
    lng: 4.6447,
    intro:
      "Velserbroek is een ruim opgezette woonkern van de gemeente Velsen, tussen Haarlem en IJmuiden. Vanuit de woonwijken bent u met de taxi snel op de A9 richting Schiphol.",
    sights: ["Recreatiegebied Spaarnwoude", "Het Zwanenwater-park", "Winkelcentrum Velserbroek"],
    streets: ["Galle Promenade", "Hagelingerweg", "Rijksweg"],
    facts: ["Velserbroek werd vanaf de jaren tachtig als nieuwe woonkern gebouwd."],
    pickups: ["Winkelcentrum Velserbroek", "Galle Promenade", "Spaarnwoude"],
  },
  {
    slug: "bloemendaal",
    name: "Bloemendaal",
    province: "Noord-Holland",
    gemeente: "Gemeente Bloemendaal",
    gemeenteUrl: "https://www.bloemendaal.nl",
    lat: 52.4072,
    lng: 4.6219,
    intro:
      "Bloemendaal ligt tussen Haarlem en de duinen, met villawijken en bosrijke lanen. Ook vanaf het strand van Bloemendaal aan Zee regelt u een taxi met vaste prijs.",
    sights: ["Nationaal Park Zuid-Kennemerland", "Bloemendaal aan Zee", "Buitenplaats Elswout"],
    streets: ["Bloemendaalseweg", "Kennemerweg", "Zeeweg"],
    facts: ["Tot de gemeente Bloemendaal horen ook Overveen, Aerdenhout, Vogelenzang en Bennebroek."],
    pickups: ["Station Bloemendaal", "Bloemendaal aan Zee", "Bloemendaalseweg"],
  },
  {
    slug: "santpoort",
    name: "Santpoort",
    province: "Noord-Holland",
    gemeente: "Gemeente Velsen",
    gemeenteUrl: "https://www.velsen.nl",
    lat: 52.4339,
    lng: 4.6403,
    intro:
      "Santpoort bestaat uit Santpoort-Noord en Santpoort-Zuid, aan de rand van de duinen in de gemeente Velsen. Beide kernen hebben een eigen station, maar met bagage is een taxi aan de deur een stuk eenvoudiger.",
    sights: ["De Ruïne van Brederode", "Landgoed Duin en Kruidberg", "Buitenplaats Spaarnberg"],
    streets: ["Hoofdstraat", "Burgemeester Enschedélaan", "Duin- en Kruidbergerweg"],
    facts: ["Santpoort-Noord en Santpoort-Zuid hebben elk een eigen NS-station."],
    pickups: ["Station Santpoort-Noord", "Station Santpoort-Zuid", "Hoofdstraat"],
  },
];

/** Kernplaatsen plus de volledige Zuid-Hollandse woonplaatsendekking. */
export const PLACES: LocalPlace[] = [...CORE_PLACES, ...ZH_PLACES];

export const PLACES_BY_SLUG = new Map(PLACES.map((p) => [p.slug, p]));

export const PROVINCES = ["Noord-Holland", "Zuid-Holland", "Zeeland", "Utrecht"] as const;
